import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CurrencyInr, Receipt, TrendUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, CHART_COLORS, fmtErr, inr } from "../lib/api";
import StatCard from "../components/StatCard";

export default function Insights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/insights")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(fmtErr(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading)
    return (
      <div className="flex h-64 items-center justify-center" data-testid="insights-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  if (!data) return null;

  const gstBars = [
    { head: "CGST", output: data.gst.output.cgst, input: data.gst.input.cgst },
    { head: "SGST", output: data.gst.output.sgst, input: data.gst.input.sgst },
    { head: "IGST", output: data.gst.output.igst, input: data.gst.input.igst },
  ];
  const totalProfit = data.profit_trend.reduce((s, m) => s + m.profit, 0);

  return (
    <div className="space-y-6" data-testid="insights-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Insights</h1>
        <p className="text-sm text-muted-foreground">Profitability, GST position and what sells best</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard title="Profit (6 Months)" value={inr(totalProfit)} icon={TrendUp} testId="stat-profit-6m" />
        <StatCard title="Output GST Collected" value={inr(data.gst.output.total)} icon={Receipt} testId="stat-gst-output" />
        <StatCard
          title="Net GST Liability"
          value={inr(data.gst.net_liability)}
          icon={CurrencyInr}
          hint={data.gst.net_liability >= 0 ? "Payable to government" : "ITC credit available"}
          testId="stat-gst-liability"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-lg border bg-card p-6" data-testid="profit-trend-chart">
          <h2 className="font-heading text-lg font-semibold tracking-tight">Monthly Profit</h2>
          <p className="mb-4 text-xs text-muted-foreground">Sales − Purchases − Expenses (excl. GST)</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.profit_trend} margin={{ left: 0, right: 8, top: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} width={70} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => inr(v)} />
                <Legend />
                <Bar dataKey="sales" name="Sales" fill="#0A2540" radius={[2, 2, 0, 0]} />
                <Bar dataKey="purchases" name="Purchases" fill="#B7791F" radius={[2, 2, 0, 0]} />
                <Bar dataKey="profit" name="Profit" fill="#008A52" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-lg border bg-card p-6" data-testid="gst-chart">
          <h2 className="font-heading text-lg font-semibold tracking-tight">GST: Output vs Input Credit</h2>
          <p className="mb-4 text-xs text-muted-foreground">Tax collected on sales vs credit from purchases &amp; expenses</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={gstBars} margin={{ left: 0, right: 8, top: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="head" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} width={70} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => inr(v)} />
                <Legend />
                <Bar dataKey="output" name="Output GST" fill="#E02424" radius={[2, 2, 0, 0]} />
                <Bar dataKey="input" name="Input Credit" fill="#008A52" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-lg border bg-card p-6" data-testid="top-items-chart">
          <h2 className="font-heading text-lg font-semibold tracking-tight">Top Selling Items</h2>
          <p className="mb-4 text-xs text-muted-foreground">By invoice value</p>
          {data.top_items.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">No sales yet.</p>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.top_items} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(v) => inr(v)} />
                  <Bar dataKey="amount" name="Sales" fill="#0A2540" radius={[0, 2, 2, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="rounded-lg border bg-card p-6" data-testid="payment-status-chart">
          <h2 className="font-heading text-lg font-semibold tracking-tight">Invoice Payment Status</h2>
          <p className="mb-4 text-xs text-muted-foreground">Sales invoice value by collection status</p>
          {data.payment_status.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">No invoices yet.</p>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.payment_status} dataKey="amount" nameKey="status" innerRadius={50} outerRadius={80} paddingAngle={2}>
                    {data.payment_status.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => inr(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
